import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DollarSign } from "lucide-react";
import { platformRevenue, revenueCsvUrl } from "../../api/platformRevenue";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { RevenueRow } from "./RevenueRow";

const Stat = ({ label, value, accent }) => (
  <div className="neu-in rounded-2xl p-4">
    <p className="text-[10px] tracking-normal text-muted-foreground">{label}</p>
    <p className={`mt-1 font-mono text-xl font-bold ${accent ? "text-primary" : ""}`}>{value}</p>
  </div>
);

export const RevenueCard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    platformRevenue(6).then(setData);
  }, []);

  if (!data) return null;
  const cur = (data.currency || "usd").toUpperCase();
  const totals = data.totals;

  return (
    <NeuCard data-testid="revenue-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <DollarSign className="h-5 w-5 text-primary" />
          <div>
            <h2 className="text-lg font-black">{t("revenue.title")}</h2>
            <p className="text-xs text-muted-foreground">
              {t("revenue.subtitle")} · {data.month}
            </p>
          </div>
        </div>
        <a href={revenueCsvUrl()} target="_blank" rel="noreferrer" data-testid="revenue-export">
          <NeuButton className="px-4 py-2 text-xs">{t("audit.export")}</NeuButton>
        </a>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="revenue-totals">
        <Stat label={t("revenue.paidMonth")} value={`${totals.paid_month} ${cur}`} accent />
        <Stat label={t("revenue.prevMonth")} value={`${totals.paid_prev_month} ${cur}`} />
        <Stat label={t("revenue.growth")} value={`${totals.growth_percent}%`} accent />
        <Stat label={t("revenue.mrrTotal")} value={`${totals.mrr} ${cur}`} />
        <Stat label={t("revenue.arr")} value={`${totals.arr} ${cur}`} />
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="revenue-trend">
        {data.trend.map((point) => (
          <div key={point.month} className="neu-in rounded-xl px-3 py-2 font-mono text-xs">
            <span className="text-muted-foreground">{point.month}</span>{" "}
            <span className="font-bold">{point.paid} {cur}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-2" data-testid="revenue-list">
        {data.companies.map((row) => (
          <RevenueRow key={row.company_id} row={row} currency={cur} />
        ))}
        {!data.companies.length ? <p className="text-sm text-muted-foreground">{t("revenue.empty")}</p> : null}
      </div>
    </NeuCard>
  );
};
